import { defineStore } from 'pinia';
import { ref } from 'vue';
import { apiFetch } from '../services/api';
import api from '../services/api';
import { brand } from '../config/brand';

export interface FaviconSettings {
  type: 'generated' | 'custom';
  color: string;
  textColor: string;
  customUrl?: string;
}

export const useSystemStore = defineStore('system', () => {
  const name = ref(brand.platformName);
  const imageUrl = ref<string | undefined>(undefined);
  const faviconType = ref<'generated' | 'custom'>('generated');
  const faviconColor = ref('#3B82F6');
  const faviconTextColor = ref('#FFFFFF');
  const faviconCustomUrl = ref<string | undefined>(undefined);

  async function load() {
    try {
      const settings = await apiFetch('/system/settings');
      name.value = settings.name || brand.platformName;
      // Always attempt the image fetch — the server 404s cleanly when no
      // custom image is set, and this stays correct even if the settings
      // flag is stale or disagrees with where the file lives.
      try {
        const resp = await api.get('/system/settings/image', { responseType: 'blob' });
        const data = resp?.data;
        if (!data) {
          imageUrl.value = undefined;
          return;
        }
        const blob: Blob =
          data instanceof Blob
            ? data
            : new Blob([data], {
                type: typeof data === 'string' ? 'application/json' : undefined,
              });
        if (!blob.size || (blob.type && blob.type.includes('json'))) {
          imageUrl.value = undefined;
          return;
        }
        if (imageUrl.value?.startsWith('blob:')) {
          URL.revokeObjectURL(imageUrl.value);
        }
        imageUrl.value = URL.createObjectURL(blob);
      } catch {
        // Missing custom image is normal — use default Otto icon.
        imageUrl.value = undefined;
      }
    } catch (err) {
      console.error('Failed to load system settings:', err);
    }
  }

  async function loadFaviconSettings() {
    try {
      const settings = await apiFetch('/system/settings/favicon');
      faviconType.value = settings.type || 'generated';
      faviconColor.value = settings.color || '#3B82F6';
      faviconTextColor.value = settings.textColor || '#FFFFFF';
      faviconCustomUrl.value = settings.customUrl || undefined;
    } catch (err) {
      console.error('Failed to load favicon settings:', err);
    }
  }

  function setFavicon(type: 'generated' | 'custom', color?: string, textColor?: string, customUrl?: string) {
    faviconType.value = type;
    if (color !== undefined) faviconColor.value = color;
    if (textColor !== undefined) faviconTextColor.value = textColor;
    if (customUrl !== undefined) faviconCustomUrl.value = customUrl;
  }

  return {
    name,
    imageUrl,
    faviconType,
    faviconColor,
    faviconTextColor,
    faviconCustomUrl,
    load,
    loadFaviconSettings,
    setFavicon,
  };
});
